import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FaLocationDot, FaUser } from "react-icons/fa6";
import { API_empleos, API_cursos, API_recursos } from "../../config/apiConfig";
import { useAuth } from "../../context/AuthContext";
import "../../pages/user/css/Empleo.css";

const WidgetUnificado = ({ categoria }) => {
  const { token } = useAuth();
  const [ofertas, setOfertas] = useState([]);
  const [cursos, setCursos] = useState([]);
  const [recursos, setRecursos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch de todas las APIs en paralelo
        const [resEmpleo, resCursos, resRecursos] = await Promise.all([
          fetch(API_empleos, {
            headers: {
              "x-auth-token": token,
            },
          }),
          fetch(API_cursos, {
            headers: {
              "x-auth-token": token,
            },
          }),
          fetch(API_recursos),
        ]);

        const [dataEmpleo, dataCursos, dataRecursos] = await Promise.all([resEmpleo.json(), resCursos.json(), resRecursos.json()]);

        // Filtrar según la categoría seleccionada
        if (categoria === "destacados") {
          // Mostrar SOLO destacados de las tres categorías
          const ofertas = dataEmpleo.filter((oferta) => oferta.isFeatured);
          const cursos = dataCursos.filter((curso) => curso.isFeatured);
          const recursos = dataRecursos.filter((recurso) => {
            return recurso.isFeatured;
          });

          setOfertas(ofertas);
          setCursos(cursos);
          setRecursos(recursos);
        } else if (categoria === "empleo") {
          // Mostrar TODAS las ofertas de empleo
          setOfertas(dataEmpleo);
          setCursos([]);
          setRecursos([]);
        } else if (categoria === "formacion") {
          // Mostrar TODOS los cursos
          setOfertas([]);
          setCursos(dataCursos);
          setRecursos([]);
        } else if (categoria === "social") {
          // Mostrar TODOS los recursos sociales
          const normalizar = (str) =>
            str
              ?.normalize("NFD")
              .replace(/[\u0300-\u036f]/g, "")
              .toLowerCase();

          const categoriasValidas = ["vivienda", "prestaciones", "subvenciones", "social"];
          const recursos = dataRecursos.filter((recurso) => {
            const categoriaRecurso = normalizar(recurso.category);
            return categoriasValidas.includes(categoriaRecurso);
          });

          setOfertas([]);
          setCursos([]);
          setRecursos(recursos);
        }
      } catch (error) {
        console.error("Error al cargar datos:", error);
        setOfertas([]);
        setCursos([]);
        setRecursos([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [categoria]);

  // Función para formatear fechas
  const formatearFecha = (fecha) => {
    const date = new Date(fecha);
    return date.toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  // Función para normalizar texto
  const normalizar = (str) =>
    str
      ?.normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase();

  // Función para obtener la ruta correcta del recurso
  const getRutaRecurso = (recurso) => {
    const categoriasSociales = ["vivienda", "prestaciones", "subvenciones", "social"];
    const normalizada = normalizar(recurso.category);
    return categoriasSociales.includes(normalizada) ? `/recurso-social/${recurso._id}` : `/recurso/${recurso._id}`;
  };

  // Función para obtener la clase de color del recurso
  const getClaseColor = (recurso) => {
    const cat = normalizar(recurso.category);
    if (cat === "empleo") return "verde";
    if (cat === "formacion") return "rosa";
    if (["vivienda", "prestaciones", "subvenciones", "social"].includes(cat)) return "azul";
    return "naranja";
  };

  // Renderizar ofertas de empleo
  const renderOfertas = () =>
    ofertas.map((oferta) => (
      <div key={oferta._id} className="recurso">
        <p className="etiqueta verde">#Oferta de empleo</p>
        <h2 className="mayusculas mt-20">{oferta.title}</h2>
        <p>{oferta.company}</p>
        <p className="fecha">Publicado el: {formatearFecha(oferta.publicationDate)}</p>
        <p className="align-center gap-5 ubicacion">
          <FaLocationDot />
          {oferta.location}
        </p>
        <Link className="verde" to={`/empleo/${oferta._id}`} aria-label="Más información sobre la oferta de empleo">
          Más información
        </Link>
      </div>
    ));

  // Renderizar cursos
  const renderCursos = () =>
    cursos.map((curso, index) => (
      <div key={index} className="recurso formacion">
        <p className="etiqueta rosa">#Curso</p>
        <h2 className="mayusculas mt-20">{curso.title}</h2>
        <p>{curso.institutionalFacility}</p>
        <p className="fecha">
          {formatearFecha(curso.startDate)} - {formatearFecha(curso.endDate)}
        </p>
        <p className="align-center gap-5 ubicacion">
          <FaUser />
          {curso.targetUsers}
        </p>
        <Link className="rosa" to={`/formacion/${curso._id}`} aria-label="Más información sobre el curso">
          Más información
        </Link>
      </div>
    ));

  // Renderizar recursos sociales
  const renderRecursos = () =>
    recursos.map((recurso) => {
      const claseColor = getClaseColor(recurso);
      const categoria = recurso.category?.charAt(0).toUpperCase() + recurso.category?.slice(1).toLowerCase();

      return (
        <div key={recurso._id} className={`recurso ${claseColor}`}>
          <p className={`etiqueta ${claseColor}`}>#{categoria}</p>
          <h2 className="negro mayusculas mt-20">{recurso.title}</h2>
          <Link className={claseColor} to={getRutaRecurso(recurso)} aria-label="Más información sobre el recurso">
            Más información
          </Link>
        </div>
      );
    });

  if (loading) return <p>Cargando contenido...</p>;

  const totalItems = ofertas.length + cursos.length + recursos.length;
  if (totalItems === 0) {
    return <p className="mt-30">Lo sentimos, pero en este momento no tenemos contenido con estas características.</p>;
  }

  return (
    <div className="carrusel wrap w-100">
      {renderOfertas()}
      {renderCursos()}
      {renderRecursos()}
    </div>
  );
};

export default WidgetUnificado;
